<template>
  <div
    :class="[
      $style.raceTableBlock,
      $style.strapiRaceTableBlock,
      { [$style.strapiRaceTableCustom]: isNetworkCustom },
    ]"
  >
    <div :class="$style.raceTableBlockThead">
      <span
        v-if="!isNetworkCustom"
        :class="[$style.raceTabletTdNumber, $style.raceTabletTd, $style.headTitle]"
      >
        {{ racesData.levelTitle }}
      </span>
      <span
        v-if="!isNetworkCustom"
        :class="[$style.raceTabletTdPlayer, $style.raceTabletTd, $style.headTitle]"
      >
        {{ racesData.playerTitle }}
      </span>
      <span v-else :class="[$style.raceTabletTdPlayer, $style.raceTabletTd, $style.headTitle]">
        {{ racesData.customNetworkTitleNumOfPrizes }}
      </span>
      <span :class="[$style.raceTabletTdPrizes, $style.raceTabletTd, $style.headTitle]">
        {{ racesData.prizeTitle }}
      </span>
      <span
        v-if="isNetworkCustom"
        :class="[$style.raceTabletTdSum, $style.raceTabletTd, $style.headTitle]"
      >
        {{ racesData.customNetworkTitleSum }}
      </span>
    </div>
    <BitTableWrapper :class="$style.raceTableTbody">
      <div
        v-for="(position, index) of slicedData"
        :key="index"
        :class="$style.raceTableTr"
        :data-place="index + 1"
      >
        <span v-if="!isNetworkCustom" :class="[$style.raceTabletTd, $style.raceTabletTdNumber]">
          <span>{{ index + 1 }}</span>
        </span>
        <span :class="[$style.raceTabletTd, $style.raceTabletTdPlayer]">
          {{ position.winner }}
        </span>
        <span
          :class="[
            $style.raceTabletTd,
            $style.raceTabletTdPrizes,
            { [$style.raceTabletTdHighlight]: !isNetworkCustom },
          ]"
        >
          {{ snippet.format(position.prize) }}
        </span>
        <span
          v-if="isNetworkCustom && position.networkCustomSum"
          :class="[$style.raceTabletTd, $style.raceTabletTdSum, $style.raceTabletTdHighlight]"
        >
          {{ snippet.format(position.networkCustomSum) }}
        </span>
      </div>
    </BitTableWrapper>
    <BitButton
      v-if="isMoreButtonShown || isLessButtonShown"
      :key="triggerUpdate"
      type="secondary"
      is-fluid-width
      :class="[$style.showMoreLessBtn, { [$style.isLessButtonShown]: isLessButtonShown }]"
      @click="toggleShowMore"
    >
      {{ isMoreButtonShown ? showMoreTitle : showLessTitle }}
      <ArrowDownLess />
    </BitButton>
    <div v-if="!isNetworkCustom" :class="$style.strapiRaceTableBlockLeaderboard">
      {{ racesData.networkRaceLeaderboardText }}
    </div>
  </div>
</template>

<script lang="ts" setup>
import { BitButton, BitTableWrapper } from '@bit/ui-kit';
import { computed, watch } from 'vue';
import { useRaceTablePagination } from '~/composables/useRaceTablePagination';
import { useSnippet } from '~/composables/useSnippet';
import ArrowDownLess from '~/public/images/common/arrowDownLess.svg?component';
import { useStoreButtonTitles } from '~/stores/strapi/buttonTitles';
import { useStoreComponents } from '~/stores/strapi/components';
import type { ITournamentExtended } from '~/types/races';

const PLAYER_COUNT_TO_SHOW = 11;

const props = defineProps({
  raceId: {
    type: String,
    requred: true,
    default: '',
  },
  tournamentExtended: {
    type: Object as PropType<ITournamentExtended>,
    default: null,
  },
  isGamePage: {
    type: Boolean,
    default: false,
  },
  isNetworkCustom: {
    type: Boolean,
    default: false,
  },
});

const storeComponents = useStoreComponents();
const storeButtons = useStoreButtonTitles();
const snippet = useSnippet();

const customLeaderboardTable = computed(() => {
  if (!props.tournamentExtended?.raceData.raceTableCustom?.length) {
    return [];
  }
  return props.tournamentExtended.raceData.raceTableCustom;
});

const racesData = computed(() => storeComponents.races);
const showMoreTitle = computed(() => storeButtons.showMoreGames);
const showLessTitle = computed(() => storeButtons.showLessGames);

const { isLessButtonShown, isMoreButtonShown, toggleShowMore, triggerUpdate, slicedData, close } =
  useRaceTablePagination<typeof customLeaderboardTable.value>(
    customLeaderboardTable,
    PLAYER_COUNT_TO_SHOW
  );

watch(
  () => props.raceId,
  () => close()
);
</script>

<style lang="scss" module src="./table.scss"></style>
